import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { Alert, buttonStyles } from "../components/ui";
import { Logo } from "../components/icons";
import { LanguageSwitch } from "../components/LanguageSwitch";
import { api, signInUrl } from "../lib/api";
import { formatMoney, formatRate } from "../lib/money";
import type { Program } from "../lib/types";
import { useTranslation } from "react-i18next";

/*
 * The reasons the content API sends somebody back here with, as sentences.
 *
 * `cancelled` is a decision rather than a failure and must not read like one —
 * somebody pressed "Cancel" on the consent screen, and telling them something
 * went wrong would be false. The other two are the same class of thing said at
 * different distances: a spent state and a refused exchange.
 */
/* Keys under `login.reasons`; anything else the callback reports reads as `failed`. */
const REASONS = new Set(["cancelled", "expired", "failed"]);

/**
 * A split page: what the programme is on the left, the way in on the right.
 *
 * ---------------------------------------------------------------------------
 * Why this one is split when the customer dashboard's is a centred column
 * ---------------------------------------------------------------------------
 * Because of who arrives at it. Somebody opening `app.maxlayer.cloud/login`
 * already has an account and knows what is behind the form; the page's whole
 * job is to get out of the way. Somebody opening this one may never have heard
 * of the programme — this is the top of a funnel, reached from a footer link on
 * the marketing site — and a bare "Continue with Maxlayer" asks them to sign in
 * to something nobody has described.
 *
 * CONVENTIONS.md makes the split layout the *operator console's* signature and
 * calls it a safety property rather than a decoration: an operator looking at
 * somebody else's data should be able to tell at a glance which console they
 * are on. That property survives this. Admin is dark and amber; this is light
 * and blue, on a different registrable domain. The two are not confusable by
 * anybody who has seen either, and the layout was never the only distinguisher.
 *
 * ---------------------------------------------------------------------------
 * The numbers are fetched, not written here
 * ---------------------------------------------------------------------------
 * `GET /v1/affiliate/program` needs no session, which is what makes this
 * possible — and the alternative is the failure this workspace keeps finding:
 * a rate typed into marketing copy disagrees with the code the first time an
 * operator changes it. What is written in this file is the *shape* of the
 * offer; every figure in it comes from the row an operator edits.
 *
 * The panel renders without them too. A visitor whose sign-in page cannot reach
 * the API should still be able to press the button — the API being down is
 * exactly when a returning affiliate is most likely to try — so the terms
 * degrade to their headings and nothing blocks.
 */
export function LoginPage() {
  const { t } = useTranslation("login");
  const [params] = useSearchParams();
  const error = params.get("error");
  // The path they were trying to open, so signing in lands there rather than on
  // the overview. Checked as a path at the far end, so a crafted value cannot
  // bounce anybody elsewhere; sent as-is here.
  const returnTo = params.get("return_to") ?? "/";

  const { data } = useQuery({
    queryKey: ["program"],
    queryFn: () => api<{ program: Program }>("/v1/affiliate/program"),
    // One attempt. This is decoration on a page whose one control works without
    // it, and three retries behind a backoff would delay nothing anybody wants.
    retry: false,
    staleTime: 300_000,
  });
  const program = data?.program;

  return (
    <div className="grid min-h-full lg:grid-cols-[1.1fr_1fr]">
      {/*
       * The pitch. Second in the DOM on a phone and first on a desktop, which
       * is the one thing this layout has to get right: a login page is visited
       * mostly by people who already know what it is, so the control comes
       * first when the two cannot sit side by side. `order` rather than two
       * copies of the markup.
       */}
      <section className="auth-wash order-2 flex flex-col justify-center border-t border-line bg-surface px-6 py-12 lg:order-1 lg:border-t-0 lg:border-r lg:px-12 xl:px-20">
        <div className="mx-auto w-full max-w-lg space-y-10">
          {/*
           * The number is the headline, not a detail in step three.
           *
           * This panel first said "Earn from the customers you bring to
           * Maxlayer" and put the rate four paragraphs down, which is a page
           * about the programme rather than an offer. Somebody who followed a
           * footer link decides in about two seconds, and the thing they decide
           * on is the figure.
           *
           * "first top-up" is doing honest work in that sentence: it says
           * plainly that this is once per customer rather than a share of
           * everything they ever spend.
           *
           * The cap is deliberately not here, and it is the one omission on
           * this page worth knowing about. At 50% and $100 it binds above a
           * $200 first top-up, so the headline overstates a large referral —
           * which on most affiliate programmes is where the complaint comes
           * from.
           *
           * What makes it acceptable is *when* the cap is read rather than
           * whether. It is on the overview, in the sentence under the greeting,
           * and on the payouts page; both are seen the moment somebody signs in
           * and long before they have shared a link. Nobody promotes this under
           * a number they were not shown. If that ever stops being true — a
           * marketing page that quotes the rate without the terms behind it —
           * the cap belongs back on this panel.
           */}
          <div className="space-y-4">
            <p className="micro">{t("eyebrow")}</p>
            <h2 className="text-3xl leading-[1.15] font-light tracking-[-0.025em] text-fg sm:text-4xl">
              {program
                ? t("headlineRate", { rate: formatRate(program.commissionBps) })
                : t("headline")}
            </h2>
          </div>

          <ol className="space-y-5">
            <Step n={1} title={t("steps.share.title")} body={t("steps.share.body")} />
            <Step
              n={2}
              title={t("steps.signup.title")}
              body={t("steps.signup.body")}
            />
            <Step
              n={3}
              title={t("steps.earn.title")}
              body={
                program
                  ? t("steps.earn.bodyFrom", { minimum: formatMoney(program.minPayoutMicros) })
                  : t("steps.earn.body")
              }
            />
          </ol>
        </div>
      </section>

      {/* The way in. */}
      <section className="order-1 flex flex-col justify-center px-6 py-12 lg:order-2 lg:px-12">
        <div className="mx-auto w-full max-w-sm space-y-8">
          <div className="space-y-3">
            {/* The mark with the word beside it, matching the shell's header.
                Alone it identifies Maxlayer and not which Maxlayer surface,
                which on the one page somebody reaches from a link they were
                sent is the question actually being asked. */}
            <div className="flex items-center gap-2">
              <Logo className="size-6 shrink-0" />
              <span className="text-[13px] font-medium text-fg">{t("brand")}</span>
            </div>
            <h1 className="text-title text-fg">{t("title")}</h1>
            <p className="text-[13px] text-fg-muted">
              {t("subtitle")}
            </p>
          </div>

          {error && (
            <Alert tone={error === "cancelled" ? "info" : "error"}>
              {t(`reasons.${REASONS.has(error) ? error : "failed"}` as "reasons.failed")}
            </Alert>
          )}

          {/*
           * An anchor rather than a button with a handler, and that is not a
           * detail. Every step of this flow is a top-level navigation — which
           * is the whole reason it replaced a cross-site cookie handshake — so
           * the control that starts it is a link, and it works with a middle
           * click, a long press and no JavaScript at all.
           */}
          <a className={buttonStyles("primary", "xl", "w-full")} href={signInUrl(returnTo)}>
            {t("continue")}
          </a>

          <p className="text-[12px] leading-relaxed text-fg-subtle">
            {t("consent")}
          </p>

          {program?.termsUrl && (
            <p className="text-[12px]">
              <a
                className="text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
                href={program.termsUrl}
                target="_blank"
                rel="noreferrer"
              >
                {t("terms")}
              </a>
            </p>
          )}

          {/* Reachable signed out: this is the one page a visitor lands on
              from a link, and the language they read it in is decided here. */}
          <LanguageSwitch className="text-[12px]" />
        </div>
      </section>
    </div>
  );
}

/**
 * One numbered step.
 *
 * The number is a plain character in a bordered square rather than an icon.
 * Three icons here would each have to mean something — a link, a person, a
 * coin — and the reader would spend a moment decoding pictures that carry less
 * than the words beside them already do.
 */
function Step({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <li className="flex gap-4">
      <span
        className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md border border-line bg-canvas font-mono text-[13px] text-fg-muted"
        aria-hidden="true"
      >
        {n}
      </span>
      <div className="space-y-1">
        <p className="text-sm font-medium text-fg">{title}</p>
        <p className="text-[13px] leading-relaxed text-fg-muted">{body}</p>
      </div>
    </li>
  );
}
